import React, { useMemo } from 'react';
import forEach from 'lodash/forEach';

import GoogleMap from '../../components/Map/GoogleMap';
import Icon from '../../icons';
import { COLORS, STEP_TYPE } from '../../constants';
import { stopMatchesFilter, isStopFilterEmpty } from '../../utils/stopFilter';

const RouteMap = ({
  mapData,
  vehicles = [],
  routes = [],
  onClickStep,
  active,
  hideDriverIds,
  allOrders = [],
  stopFilter = null,
  searchTicketsMap = '',
}) => {
  const { markers, polylines } = useMemo(() => {
    let newMarkers = [];
    const newPolylines = [];

    if (mapData) {
      forEach(routes, (route) => {
        const mapRoute = mapData[route.driver.id];
        if (hideDriverIds?.includes(route.driver.id)) return;
        if (!mapRoute) return;

        const steps = mapRoute.steps;
        const polyline = mapRoute.polyline;
        const color = mapRoute.color;
        forEach(steps, (step, indexStep) => {
          let className = '';
          let textColor = color;
          let backgroundColor = '';
          if (step.type === STEP_TYPE.pickup && step.status === 'COMPLETED') {
            const orderIds = step.orderIds || [];
            const isCompleted = orderIds.some((id) => {
              const order = allOrders.find((o) => o.id === id);
              return order && order.pickupStatus === 'COLLECTED';
            });
            textColor = 'white';

            if (isCompleted) {
              className = 'completed';
              backgroundColor = '#437444'; // Green for completed pickups
            } else {
              className = 'failed';
              backgroundColor = '#b03030'; // Red for failed pickups
            }
          } else if (step.type === STEP_TYPE.delivery && step.status === 'COMPLETED') {
            // For delivery, check if any order in orderIds is completed
            const orderIds = step.orderIds || (step.orderId ? [step.orderId] : []);
            const isCompleted = orderIds.some((id) => {
              const order = allOrders.find((o) => o.id === id);
              return order && order.status === 'COMPLETED';
            });
            textColor = 'white';
            if (isCompleted) {
              className = 'completed';
              backgroundColor = '#437444'; // Green for completed deliveries
            } else {
              className = 'failed';
              backgroundColor = '#b03030'; // Red for failed deliveries
            }
          }
          newMarkers.push({
            ...step,
            isUnscheduled: false,
            isStop: true,
            stopNumber: indexStep + 1,
            content: (
              <Icon
                type={step.type === STEP_TYPE.pickup ? 'MarkerPickup' : 'MarkerDropoff'}
                text={indexStep + 1}
                color={backgroundColor || '#fff'}
                strokeColor={backgroundColor ? backgroundColor : color}
                textColor={textColor}
                className={className}
              />
            ),
            latitude: Number(step.lat),
            longitude: Number(step.lng),
            offsetX: 1,
            offsetY: -6,
            duration: STEP_TYPE.pickup === step.type ? step.pickupDuration : step.deliveryDuration,
            onClick: () => onClickStep(step),
            color: color,
            driverId: route.driver.id,
          });
        });
        if (polyline) {
          console.log('[RouteMap] Adding polyline:', {
            routeId: route.id,
            driverId: route.driver.id,
            polyline: polyline,
            polylineLength: polyline.length,
            color: color,
          });
          newPolylines.push({
            polyline: polyline,
            color: color,
            strokeWeight: 2,
            strokeOpacity: 0.8,
          });
        }
      });
      if (mapData['unscheduled']?.steps.length > 0) {
        forEach(mapData['unscheduled']?.steps, (stop, index) => {
          const stopOrder = index + 1;
          newMarkers.push({
            ...stop,
            content: (
              <Icon
                type={stop.type === STEP_TYPE.pickup ? 'MarkerPickup' : 'MarkerDropoff'}
                text={stopOrder}
                color={'#fff'}
                strokeColor={stop.type === STEP_TYPE.service ? '#722ed1' : '#606060'}
              />
            ),
            isUnscheduled: true,
            latitude: Number(stop.lat),
            longitude: Number(stop.lng),
            offsetX: 1,
            offsetY: -6,
            onClick: () => onClickStep(stop),
          });
        });
      }

      // OVO-149 — the filter now applies to SCHEDULED stops too (it used to
      // keep every `!isUnscheduled` marker, so filtering by partner left the
      // map unreadable on a busy day). Non-matching pins are hidden; vehicle
      // start/end markers are added after this block and stay. Running the
      // filter BEFORE the co-located merge below means a merged pin survives
      // as long as ANY of its visits matches — and its label then lists
      // exactly the matching stop numbers.
      // #174 (Oct 26 2026) — the whole filter (text + partners + zips) applies
      // to scheduled pins too (Sep 7 had limited them to the text; CT filters
      // assigned stops by zone). Unscheduled pins follow the unscheduled LIST's rule — order-level OR:
      // an order stays (both its pins) when ANY of its legs matches, so a zip
      // picked on the delivery side never strips the depot pickup pin of the
      // very order the list keeps.
      if (!isStopFilterEmpty(stopFilter)) {
        const keptUnscheduledOrders = new Set(
          newMarkers.filter((m) => m.isUnscheduled && stopMatchesFilter(m, stopFilter)).map((m) => m.orderCode),
        );
        newMarkers = newMarkers.filter((marker) =>
          marker.isUnscheduled ? keptUnscheduledOrders.has(marker.orderCode) : stopMatchesFilter(marker, stopFilter),
        );
      }
    }

    // Track how many vehicles share identical start/end coordinates so we can offset them.
    const vehicleLocCounts = {};
    const vehicleLocKey = (lat, lng) => `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;
    // Spread ~40m per marker so they fan out without leaving the city block.
    const OFFSET_DEG = 0.00035;

    // The route's start/end depot coordinates (visible vehicles only). When a
    // merged stop pin sits on the start/end (e.g. this route's depot is the
    // bike's start, its end AND the reload point for pickups 1·4·7), we fold the
    // timeline's start/end glyph into that pin and skip the separate start/end
    // marker below — one combined badge instead of overlapping markers.
    // key → the vehicle start/end marker ids at that location, so a folded pin
    // can light up (m-active) when the timeline start/end icon is clicked too.
    // #208 — keyed per route (driver) AND location: a pin folds only its own route's start/end.
    const routeLocKey = (driverId, lat, lng) => `${driverId}|${vehicleLocKey(lat, lng)}`;
    const startKeyIds = {};
    const endKeyIds = {};
    forEach(vehicles, (v) => {
      if (hideDriverIds?.includes(v.driver.id)) return;
      const sk = routeLocKey(v.driver.id, v.startLat, v.startLng);
      (startKeyIds[sk] = startKeyIds[sk] || []).push(`${v.id}-start`);
      if (v.endAddress && v.endLat && v.endLng) {
        const ek = routeLocKey(v.driver.id, v.endLat, v.endLng);
        (endKeyIds[ek] = endKeyIds[ek] || []).push(`${v.id}-end`);
      }
    });
    const absorbedStartKeys = new Set();
    const absorbedEndKeys = new Set();

    // Stops that share an EXACT location (e.g. the depot the bike returns to to
    // reload between capacity batches — same place, different times) collapse
    // into ONE marker at the true coordinate, labeled with every visit's stop
    // number (e.g. "1·4·7"). Previously they were fanned out to fake offset
    // positions, which drew pins where the driver never actually goes. Clicking
    // opens the first visit; StopClicked lists the co-located siblings so each
    // is still individually reachable. Only scheduled stops merge — vehicle
    // start/end (offset below) and unscheduled markers pass through untouched.
    // #208 — only stops of the SAME route merge. Grouping across routes joined stop 18 of one
    // route and stop 27 of another into one "18·27" badge (CT: two boxes with the same GPS).
    // Pins of different routes at one spot are fanned out like the vehicle start/end markers,
    // the first one keeping the true coordinate.
    const stopGroups = {};
    const passthroughMarkers = [];
    newMarkers.forEach((m) => {
      if (!m.isStop) {
        passthroughMarkers.push(m);
        return;
      }
      const key = routeLocKey(m.driverId, m.latitude, m.longitude);
      (stopGroups[key] = stopGroups[key] || []).push(m);
    });
    const routesAtLoc = {};
    const fanOut = (marker) => {
      const loc = vehicleLocKey(marker.latitude, marker.longitude);
      const idx = routesAtLoc[loc] || 0;
      routesAtLoc[loc] = idx + 1;
      if (idx === 0) return marker;
      return {
        ...marker,
        latitude: marker.latitude + idx * OFFSET_DEG,
        longitude: marker.longitude + idx * OFFSET_DEG,
      };
    };
    const mergedStopMarkers = [];
    Object.values(stopGroups).forEach((group) => {
      if (group.length === 1) {
        mergedStopMarkers.push(fanOut(group[0]));
        return;
      }
      const ordered = [...group].sort((a, b) => (a.stopNumber || 0) - (b.stopNumber || 0));
      const first = ordered[0];
      const label = ordered.map((m) => m.stopNumber).join('·');
      const key = routeLocKey(first.driverId, first.latitude, first.longitude);
      const isRouteStart = !!startKeyIds[key];
      const isRouteEnd = !!endKeyIds[key];
      if (isRouteStart) absorbedStartKeys.add(key);
      if (isRouteEnd) absorbedEndKeys.add(key);
      const mergedStopIds = ordered.map((m) => m.id);
      mergedStopMarkers.push(
        fanOut({
          ...first,
          mergedStopIds,
          // Every id this one pin now stands for — its merged stops AND the
          // folded start/end — so the map pin animates when any of them (a
          // "Visits Here" chip, or the timeline start/end icon) is selected.
          activeIds: [
            ...mergedStopIds,
            ...(isRouteStart ? startKeyIds[key] : []),
            ...(isRouteEnd ? endKeyIds[key] : []),
          ],
          content: (
            <div className="merged-stop-badge" style={{ borderColor: first.color, color: first.color }}>
              {isRouteStart && <Icon type="RouteDetailsStart" color={first.color} className="badge-endpoint" />}
              <span className="badge-label">{label}</span>
              {isRouteEnd && <Icon type="RouteDetailsEnd" color={first.color} className="badge-endpoint" />}
            </div>
          ),
          offsetX: 0,
          offsetY: 0,
        }),
      );
    });
    newMarkers = [...passthroughMarkers, ...mergedStopMarkers];

    forEach(vehicles, (vehicle) => {
      const color = '#' + COLORS[vehicle.driver.id % COLORS.length];
      if (hideDriverIds?.includes(vehicle.driver.id)) return;
      const steps = mapData[vehicle.driver.id]?.steps || [];

      const stepsPickup = steps.filter((step) => step.type === STEP_TYPE.pickup);

      let countPickup = 0;
      forEach(stepsPickup, (step) => {
        countPickup += step.orderIds?.length;
      });

      const startKey = vehicleLocKey(vehicle.startLat, vehicle.startLng);
      // Folded into a merged stop badge of this route at the same spot — skip the duplicate.
      if (!absorbedStartKeys.has(routeLocKey(vehicle.driver.id, vehicle.startLat, vehicle.startLng))) {
        const startIdx = vehicleLocCounts[startKey] || 0;
        vehicleLocCounts[startKey] = startIdx + 1;

        newMarkers.push({
          ...vehicle,
          id: `${vehicle.id}-start`,
          content: <Icon type="MarkerStart" color={'#fff'} strokeColor={color} />,
          latitude: Number(vehicle.startLat) + startIdx * OFFSET_DEG,
          longitude: Number(vehicle.startLng) + startIdx * OFFSET_DEG,
          offsetX: 1,
          offsetY: -6,
          countPickup,
          type: 'start',
          onClick: () => onClickStep({ id: `${vehicle.id}-start`, type: 'vehicle' }),
        });
      }

      if (
        vehicle.endAddress &&
        vehicle.endLat &&
        vehicle.endLng &&
        !absorbedEndKeys.has(routeLocKey(vehicle.driver.id, vehicle.endLat, vehicle.endLng))
      ) {
        const endKey = vehicleLocKey(vehicle.endLat, vehicle.endLng);
        const endIdx = vehicleLocCounts[endKey] || 0;
        vehicleLocCounts[endKey] = endIdx + 1;
        newMarkers.push({
          ...vehicle,
          id: `${vehicle.id}-end`,
          content: <Icon type="MarkerEnd" color={'#fff'} strokeColor={color} />,
          latitude: Number(vehicle.endLat) + endIdx * OFFSET_DEG,
          longitude: Number(vehicle.endLng) + endIdx * OFFSET_DEG,
          offsetX: 1,
          offsetY: -6,
          type: 'end',
          onClick: () => onClickStep({ id: `${vehicle.id}-end`, type: 'vehicle' }),
        });
      }
    });

    console.log('[RouteMap] Returning polylines:', newPolylines.length, newPolylines);
    return { markers: newMarkers, polylines: newPolylines };
  }, [mapData, vehicles, routes, hideDriverIds, allOrders, onClickStep, stopFilter, searchTicketsMap]);

  // The day-plan already renders a single unified popup (StopClicked) for both
  // stops and vehicle start/end icons, driven by the same `selectedStep`. Letting
  // the map Marker open its own VehicleInfoWindow too produced a DOUBLE popup
  // (one "route" card on the map + one "driver" card from StopClicked). Suppress
  // the map InfoWindow here so StopClicked is the single source of truth.
  return (
    <GoogleMap
      markers={markers}
      className="mapbox-routePlan-detail"
      polylines={polylines}
      active={active}
      disableInfoWindow
    />
  );
};

export default RouteMap;
